import { displayAmount } from "./rounding";
import { dailyValue } from "./daily-values";
import { displaySupplementPercentDV } from "./supplement";
import type { Market, NutrientId, Profile } from "./types";

/** One declarable FDA core/macro nutrient (21 CFR 101.9(c) / 101.36). */
export interface CoreNutrientDef {
  nutrientId: NutrientId;
  /** Canonical FDA display name. */
  label: string;
  /** Canonical entry/display unit — the amount is stored/rounded in this unit. */
  unit: "g" | "mg";
  /** Indent level (0 top-level bold macro, 1 sub-nutrient, 2 added-sugars). */
  indent: 0 | 1 | 2;
  /** Canonical render sequence, ascending. */
  order: number;
  /**
   * Whether %DV auto-derives from the DV table when the merchant gives no override. False for
   * Protein — its %DV is optional (21 CFR 101.9(c)(7)(i) requires a PDCAAS-corrected amount before a
   * protein %DV may be declared), so we leave it blank unless the merchant supplies an explicit DV.
   */
  autoDv: boolean;
}

/**
 * The core nutrients a Supplement Facts panel may declare, in canonical FDA order. Calories are
 * NOT here — they remain a dedicated top field. Bold is DERIVED (indent === 0), never stored.
 */
export const CORE_NUTRIENTS: readonly CoreNutrientDef[] = [
  { nutrientId: "fat_total", label: "Total Fat", unit: "g", indent: 0, order: 10, autoDv: true },
  { nutrientId: "fat_sat", label: "Saturated Fat", unit: "g", indent: 1, order: 20, autoDv: true },
  { nutrientId: "fat_trans", label: "Trans Fat", unit: "g", indent: 1, order: 30, autoDv: true },
  { nutrientId: "cholesterol", label: "Cholesterol", unit: "mg", indent: 0, order: 40, autoDv: true },
  { nutrientId: "sodium", label: "Sodium", unit: "mg", indent: 0, order: 50, autoDv: true },
  { nutrientId: "carb_total", label: "Total Carbohydrate", unit: "g", indent: 0, order: 60, autoDv: true },
  { nutrientId: "fiber", label: "Dietary Fiber", unit: "g", indent: 1, order: 70, autoDv: true },
  { nutrientId: "sugars_total", label: "Total Sugars", unit: "g", indent: 1, order: 80, autoDv: true },
  { nutrientId: "sugars_added", label: "Added Sugars", unit: "g", indent: 2, order: 90, autoDv: true },
  // Protein: %DV is optional and requires a PDCAAS-corrected amount — leave blank unless overridden.
  { nutrientId: "protein", label: "Protein", unit: "g", indent: 0, order: 100, autoDv: false },
];

export const CORE_NUTRIENT_BY_ID: Record<string, CoreNutrientDef> = Object.fromEntries(
  CORE_NUTRIENTS.map((n) => [n.nutrientId, n]),
);

/** Is `id` one of the declarable core nutrients? (Type-guards to NutrientId.) */
export function isCoreNutrientId(id: string): id is NutrientId {
  return Object.prototype.hasOwnProperty.call(CORE_NUTRIENT_BY_ID, id);
}

/**
 * Amount string for a core nutrient — real per-nutrient rounding via `displayAmount`, with the
 * canonical unit appended unless the regulation phrase already embeds one ("less than 1 g").
 */
export function displayCoreNutrientAmount(nutrientId: NutrientId, amount: number): string {
  const def = CORE_NUTRIENT_BY_ID[nutrientId]!; // caller guarantees a core nutrientId
  const s = displayAmount(nutrientId, "US", amount);
  // A phrase like "less than 1 g" / "less than 5 mg" already carries a unit; a bare number doesn't.
  return /[a-z]/i.test(s) ? s : `${s} ${def.unit}`;
}

/**
 * %DV string for a core nutrient, or null when no DV applies (blank cell, NO dagger). An explicit
 * `overrideDv` (merchant-entered, same unit as amount) wins; otherwise the adult DV table is used.
 */
export function coreNutrientPercentDV(
  nutrientId: NutrientId,
  amount: number,
  overrideDv: number | null | undefined,
  market: Market = "US",
  profile: Profile = "adult",
): string | null {
  // An explicit override always wins (lets a merchant declare a PDCAAS-corrected protein %DV, etc.).
  if (overrideDv != null) {
    return overrideDv > 0 ? displaySupplementPercentDV(amount, overrideDv) : null;
  }
  // Otherwise auto-derive only for nutrients whose %DV is mandatory (not Protein) and DV-established.
  const def = CORE_NUTRIENT_BY_ID[nutrientId];
  if (!def || !def.autoDv) return null;
  const dv = dailyValue(nutrientId, market, profile);
  if (dv == null || !(dv > 0)) return null;
  return displaySupplementPercentDV(amount, dv);
}
